import { createFileRoute } from "@tanstack/react-router";
import { FALLBACK_VALUE } from "common/constants";
import { filterList } from "common/utils/search";
import { snakeCaseToTitleCase } from "common/utils/strings";
import AdminPage from "lib/components/admin/builders/AdminPage";
import BuilderHeader from "lib/components/builders/BuilderHeader";
import { DEBUG_PAGES } from "lib/routeHelpers";
import { postWithTimeout } from "lib/utils/fetchAuthHelpers";
import { useState } from "react";
import Link from "ui/components/Link";
import Input from "ui/forms/Input";
import useTabbedMenu from "ui/hooks/useTabbedMenu";
import { ConfigTable } from "ui/tables/DataTable";
import type { Column } from "ui/tables/DataTable/types";
import withAuthentication from "../../components/withAuthentication";
import styles from "./debug.module.scss";

interface DebugPage {
  page: string;
  description: string;
  link: string;
}

type AdminDebugTab = "debug_pages" | "demos" | "tools";
const FACE_DIFF_VIEWER_LINK = "/debug/face-diff-viewer";

const AdminBuildersPage = () => {
  const [search, setSearch] = useState("");
  const [isBullBoardLoading, setIsBullBoardLoading] = useState(false);
  const { tab, menuItems } = useTabbedMenu<AdminDebugTab>(["debug_pages", "demos", "tools"], {
    prettyNames: {
      debug_pages: "Debug Pages",
      demos: "Demos",
      tools: "Tools",
    },
  });

  const columns: Column<DebugPage>[] = [
    {
      field: "page",
    },
    {
      header: "Link",
      accessor: function LinkCell(row) {
        return <Link href={row.link}>{row.link}</Link>;
      },
    },
    {
      field: "description",
      width: 0.5,
    },
  ];

  const rows = DEBUG_PAGES.map((info) => ({
    page: snakeCaseToTitleCase(info.page).replace(/-/g, " "),
    description: info.description || FALLBACK_VALUE,
    link: `/debug/${info.page}`,
  }));

  const demoRows = [
    {
      page: "Connected Experience",
      description: "Demo of Showroom, Estimate, and Live Sheets.",
      link: "/demo/connected",
    },
    {
      page: "Connected - Bigscreen",
      description: "Connected experience bigscreen layout demo",
      link: "/demo/connected/bigscreen",
    },
    {
      page: "Connected - Showroom",
      description: "Connected experience showroom tab demo",
      link: "/demo/connected/showroom",
    },
    {
      page: "Connected - Single Screen",
      description: "Connected experience single screen demo",
      link: "/demo/connected/singlescreen",
    },
    {
      page: "Connected - Tabs",
      description: "Connected experience tabbed demo",
      link: "/demo/connected/tabs",
    },
    {
      page: "IBS 2023 - Start",
      description: "Entry point for the IBS 2023 demo flow",
      link: "/demo/ibs-2023-start",
    },
    {
      page: "IBS 2023 - Selections",
      description: "IBS 2023 selections demo",
      link: "/demo/ibs-2023-selections",
    },
    {
      page: "IBS 2023 - Walkthrough",
      description: "IBS 2023 walkthrough demo",
      link: "/demo/ibs-2023-walkthrough",
    },
    {
      page: "Panorama viewer demo",
      description: "Public unauthenticated demo of the panorama viewer",
      link: "/demo/photoreal-interior360-TS21DMPO80M1",
    },
    {
      page: "Selections viewer demo",
      description: "Public unauthenticated demo of the selections viewer",
      link: "/demo/photoreal-selections360-WX34VCTU45P2",
    },
    {
      page: "Photoreal - Selections demo",
      description: "Public unauthenticated demo of Orion selections",
      link: "/demo/photoreal-selections",
    },
    {
      page: "Photoreal - Walkthrough demo",
      description: "Public unauthenticated demo of Orion walkthrough",
      link: "/demo/photoreal-walkthrough",
    },
    {
      page: "Sheets",
      description: "Standalone sheets demo",
      link: "/demo/sheets",
    },
    {
      page: "Showroom",
      description: "Standalone showroom demo",
      link: "/demo/showroom",
    },
  ];

  const handleQueuesClick = async (e: React.MouseEvent<HTMLAnchorElement> | null) => {
    e?.stopPropagation();
    setIsBullBoardLoading(true);
    try {
      const resp = await postWithTimeout("/queues/session");
      if (resp.ok) {
        window.location.assign("/queues");
      }
    } finally {
      setIsBullBoardLoading(false);
    }
  };

  const toolRows = [
    {
      page: "Face Diff Viewer",
      description: "Visual face-diff comparison tool",
      link: FACE_DIFF_VIEWER_LINK,
    },
    {
      page: "Worker Queues",
      description: "Dashboard for monitoring BullMQ job queues",
      link: "/queues",
    },
  ];

  const toolColumns: Column<DebugPage>[] = [
    { field: "page" },
    {
      header: "Link",
      accessor: function LinkCell(row) {
        if (row.link === "/queues") {
          return (
            <Link disabled={isBullBoardLoading} onClick={handleQueuesClick}>
              /queues
            </Link>
          );
        }
        return <Link href={row.link}>{row.link}</Link>;
      },
    },
    { field: "description", width: 0.5 },
  ];

  const debugRows = filterList(
    rows.filter((row) => row.link !== FACE_DIFF_VIEWER_LINK),
    search,
  );
  const filteredDemoRows = filterList(demoRows, search);
  const filteredToolRows = filterList(toolRows, search);

  let currentRows = debugRows;
  let searchPlaceholder = "Search Debug Pages";

  if (tab === "demos") {
    currentRows = filteredDemoRows;
    searchPlaceholder = "Search Demos";
  } else if (tab === "tools") {
    currentRows = filteredToolRows;
    searchPlaceholder = "Search Tools";
  }

  const currentColumns = tab === "tools" ? toolColumns : columns;

  return (
    <AdminPage currentPage="debug" pageTitle="Admin - Debug Pages">
      <BuilderHeader
        header="Debug Pages"
        menuItems={menuItems}
        headerActions={
          <>
            <div style={{ flex: 1 }}>
              <Input
                style={{ width: 200, height: 36 }}
                inputSize="medium"
                placeholder={searchPlaceholder}
                value={search}
                onChange={(e) => setSearch(e.currentTarget.value)}
              />
            </div>
          </>
        }
      />
      <div className={styles.tableContainer}>
        <div className={styles.tableWrapperWithMargin}>
          <ConfigTable
            columns={currentColumns}
            data={currentRows}
            getRowHref={tab === "tools" ? undefined : (r) => r.original.link}
          />
        </div>
      </div>
    </AdminPage>
  );
};

export const Route = createFileRoute("/admin/debug")({
  component: withAuthentication(AdminBuildersPage, { superAdmin: true }),
});
